import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../../api/apiClient';
import { Search } from 'lucide-react';
import PunishmentHeader from '../../components/hris/punishment/PunishmentHeader';
import PunishmentTable from '../../components/hris/punishment/PunishmentTable';
import DeletePunishmentDialog from '../../components/hris/punishment/DeletePunishmentDialog';
import { containerVariants, itemVariants } from '../../utils/animations';
import { useToast } from '../../context/ToastContext';

export default function PunishmentList() {
  const { showSuccess, showError } = useToast();
  const [punishments, setPunishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    id: number | null;
    employeeName: string;
  }>({
    open: false,
    id: null,
    employeeName: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/hris/punishments');
      setPunishments(data.punishments || []);
      setEmployees(data.employees || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = empId => {
    return employees.find(e => e.id === empId)?.full_name || '-';
  };

  const handleDeleteClick = (id: number, employeeName: string) => {
    setDeleteDialog({
      open: true,
      id,
      employeeName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.id) return;

    try {
      await apiClient.delete(`/hris/punishments/${deleteDialog.id}`);
      showSuccess('Data hukuman berhasil dihapus');
      setDeleteDialog({ open: false, id: null, employeeName: '' });
      loadData(); // Reload data after deletion
    } catch (error: any) {
      showError(error?.message || 'Gagal menghapus data hukuman');
    }
  };

  const handleDeleteClose = () => {
    setDeleteDialog({ open: false, id: null, employeeName: '' });
  };

  const filteredData = punishments.filter(item =>
    getEmployeeName(item.employee_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <PunishmentHeader />
      </motion.div>
      <motion.div variants={itemVariants} className="bg-white rounded-lg border shadow-sm">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cari pegawai..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300"
            />
          </div>
        </div>
        <PunishmentTable
          punishments={filteredData}
          loading={loading}
          getEmployeeName={getEmployeeName}
          onDelete={handleDeleteClick}
        />
      </motion.div>
      <DeletePunishmentDialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        employeeName={deleteDialog.employeeName}
      />
    </motion.div>
  );
}
